import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Chip, MenuItem, TextField } from "@mui/material";
import {
  BLOOD_TYPES,
  MedicalInfo,
  RecordOptions,
  getRecordOptions,
} from "../../services/clientRecordsService";

export type MedicalInfoValues = {
  allergies: string[];
  conditions: string[];
  medications: string;
  bloodType: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  notes: string;
};

export const emptyMedicalInfo = (): MedicalInfoValues => ({
  allergies: [],
  conditions: [],
  medications: "",
  bloodType: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  notes: "",
});

export const toMedicalValues = (info: MedicalInfo | null): MedicalInfoValues =>
  info
    ? {
        allergies: info.allergies ?? [],
        conditions: info.conditions ?? [],
        medications: info.medications ?? "",
        bloodType: info.bloodType ?? "",
        emergencyContactName: info.emergencyContactName ?? "",
        emergencyContactPhone: info.emergencyContactPhone ?? "",
        notes: info.notes ?? "",
      }
    : emptyMedicalInfo();

/** True when nothing has been filled in (the intake step then saves nothing). */
export const isMedicalInfoEmpty = (v: MedicalInfoValues) =>
  !v.allergies.length &&
  !v.conditions.length &&
  !v.medications.trim() &&
  !v.bloodType &&
  !v.emergencyContactName.trim() &&
  !v.emergencyContactPhone.trim() &&
  !v.notes.trim();


const TagsField: React.FC<{
  label: string;
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  danger?: boolean;
}> = ({ label, value, options, onChange, danger }) => (
  <Autocomplete
    multiple
    freeSolo
    options={options}
    value={value}
    onChange={(_, next) => onChange((next as string[]).map((v) => v.trim()).filter(Boolean))}
    renderTags={(tags, getTagProps) =>
      tags.map((tag, index) => {
        const { key, ...rest } = getTagProps({ index });
        return (
          <Chip
            key={key}
            {...rest}
            size="small"
            label={tag}
            color={danger ? "error" : "default"}
            variant={danger ? "filled" : "outlined"}
          />
        );
      })
    }
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder="Type and press Enter" size="small" />
    )}
  />
);

/**
 * Medical (health, in the sports build) info. Every field is optional.
 */
const MedicalInfoFields: React.FC<{
  value: MedicalInfoValues;
  onChange: (value: MedicalInfoValues) => void;
}> = ({ value, onChange }) => {
  const set = <K extends keyof MedicalInfoValues>(key: K, next: MedicalInfoValues[K]) =>
    onChange({ ...value, [key]: next });

  // The organization's own lists (Settings); anything else can still be typed.
  const [options, setOptions] = useState<RecordOptions>({ allergies: [], conditions: [], fileTypes: [] });
  useEffect(() => {
    getRecordOptions()
      .then(setOptions)
      .catch(() => undefined);
  }, []);

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <TagsField
          label="Allergies"
          value={value.allergies}
          options={options.allergies}
          onChange={(v) => set("allergies", v)}
          danger
        />
      </Box>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <TagsField
          label="Conditions"
          value={value.conditions}
          options={options.conditions}
          onChange={(v) => set("conditions", v)}
        />
      </Box>
      <TextField
        label="Current medications"
        size="small"
        value={value.medications}
        onChange={(e) => set("medications", e.target.value)}
        multiline
        minRows={1}
        sx={{ gridColumn: "1 / -1" }}
        inputProps={{ maxLength: 2000 }}
      />
      <TextField
        select
        label="Blood type"
        size="small"
        value={value.bloodType}
        onChange={(e) => set("bloodType", e.target.value)}
      >
        <MenuItem value="">Unknown</MenuItem>
        {BLOOD_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>
      <Box />
      <TextField
        label="Emergency contact name"
        size="small"
        value={value.emergencyContactName}
        onChange={(e) => set("emergencyContactName", e.target.value)}
        inputProps={{ maxLength: 120 }}
      />
      <TextField
        label="Emergency contact phone"
        size="small"
        value={value.emergencyContactPhone}
        onChange={(e) => set("emergencyContactPhone", e.target.value)}
        inputProps={{ inputMode: "tel", maxLength: 30 }}
      />
      <TextField
        label="Other notes"
        size="small"
        value={value.notes}
        onChange={(e) => set("notes", e.target.value)}
        multiline
        minRows={2}
        sx={{ gridColumn: "1 / -1" }}
        inputProps={{ maxLength: 4000 }}
      />
    </Box>
  );
};

export default MedicalInfoFields;
